/**
 * Retail POS Runtime.
 *
 * LOT 12.7 — Retail POS End-to-End Integration
 *
 * This Runtime is an orchestration boundary only.
 *
 * It owns:
 * - no Catalog;
 * - no Cart;
 * - no Customer;
 * - no Loyalty;
 * - no Payment;
 * - no SalesReceipt;
 * - no SalesLine;
 * - no CashSession;
 * - no Inventory;
 * - no Return engine;
 * - no Refund engine.
 *
 * All dynamic capabilities are injected.
 */


export class RetailPOSRuntimeError extends Error {
  constructor(
    message,
    {
      code =
        "RETAIL_POS_RUNTIME_ERROR",

      operation = null,
      cause = null,
    } = {}
  ) {
    super(message);

    this.name =
      "RetailPOSRuntimeError";

    this.code = code;
    this.operation =
      operation;

    if (cause !== null) {
      this.cause = cause;
    }
  }
}


export function createRetailPOSRuntime({
  productResolver = null,
  productSearchProvider = null,
  cartRuntime = null,
  customerRuntime = null,
  loyaltyRuntime = null,
  sessionBridge = null,
  receiptRuntime = null,
} = {}) {
  assertOptionalFunction(
    productResolver,
    "productResolver"
  );

  assertOptionalFunction(
    productSearchProvider,
    "productSearchProvider"
  );

  assertOptionalObject(
    cartRuntime,
    "cartRuntime"
  );

  assertOptionalObject(
    customerRuntime,
    "customerRuntime"
  );

  assertOptionalObject(
    loyaltyRuntime,
    "loyaltyRuntime"
  );

  assertOptionalObject(
    sessionBridge,
    "sessionBridge"
  );

  assertOptionalObject(
    receiptRuntime,
    "receiptRuntime"
  );


  const runtime = {
    async resolveProductByCode(
      code,
      context = {}
    ) {
      assertNonEmptyString(
        code,
        "code"
      );

      assertScope(context);

      const resolver =
        requireCapability(
          productResolver,
          "productResolver",
          "resolveProductByCode"
        );

      return invoke(
        "resolveProductByCode",
        () =>
          resolver(
            code,
            createScope(context)
          )
      );
    },


    async scanProduct(
      code,
      context = {}
    ) {
      return runtime
        .resolveProductByCode(
          code,
          context
        );
    },


    async searchProducts(
      query,
      context = {}
    ) {
      assertNonEmptyString(
        query,
        "query"
      );

      assertScope(context);

      const provider =
        requireCapability(
          productSearchProvider,
          "productSearchProvider",
          "searchProducts"
        );

      const result =
        await invoke(
          "searchProducts",
          () =>
            provider(
              query,
              createScope(context)
            )
        );

      if (!Array.isArray(result)) {
        throw new RetailPOSRuntimeError(
          "Retail product search must return an array.",
          {
            code:
              "INVALID_RETAIL_POS_SEARCH_RESULT",

            operation:
              "searchProducts",
          }
        );
      }

      return result;
    },


    async loadCart(
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          cartRuntime,

        ownerName:
          "cartRuntime",

        method:
          "loadCart",

        operation:
          "loadCart",

        args: [
          createScope(context),
        ],
      });
    },


    async addProduct(
      product,
      quantity = 1,
      context = {}
    ) {
      assertScope(context);

      if (
        !product
        || typeof product
          !== "object"
      ) {
        throw new RetailPOSRuntimeError(
          "Retail product must be an object.",
          {
            code:
              "INVALID_RETAIL_PRODUCT",

            operation:
              "addProduct",
          }
        );
      }

      assertPositiveQuantity(
        quantity
      );

      return invokeCapability({
        owner:
          cartRuntime,

        ownerName:
          "cartRuntime",

        method:
          "addProduct",

        operation:
          "addProduct",

        args: [
          product,
          quantity,
          createScope(context),
        ],
      });
    },


    async decrementProduct(
      lineOrProduct,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          cartRuntime,

        ownerName:
          "cartRuntime",

        method:
          "decrementProduct",

        operation:
          "decrementProduct",

        args: [
          lineOrProduct,
          createScope(context),
        ],
      });
    },


    async removeProduct(
      lineOrProduct,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          cartRuntime,

        ownerName:
          "cartRuntime",

        method:
          "removeProduct",

        operation:
          "removeProduct",

        args: [
          lineOrProduct,
          createScope(context),
        ],
      });
    },


    async clearCart(
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          cartRuntime,

        ownerName:
          "cartRuntime",

        method:
          "clearCart",

        operation:
          "clearCart",

        args: [
          createScope(context),
        ],
      });
    },


    async resolveCustomer(
      customerReference,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          customerRuntime,

        ownerName:
          "customerRuntime",

        method:
          "resolveCustomer",

        operation:
          "resolveCustomer",

        args: [
          customerReference,
          createScope(context),
        ],
      });
    },


    async searchCustomers(
      query,
      context = {}
    ) {
      assertNonEmptyString(
        query,
        "query"
      );

      assertScope(context);

      const result =
        await invokeCapability({
          owner:
            customerRuntime,

          ownerName:
            "customerRuntime",

          method:
            "searchCustomers",

          operation:
            "searchCustomers",

          args: [
            query,
            createScope(context),
          ],
        });

      if (!Array.isArray(result)) {
        throw new RetailPOSRuntimeError(
          "Retail customer search must return an array.",
          {
            code:
              "INVALID_RETAIL_POS_CUSTOMER_SEARCH_RESULT",

            operation:
              "searchCustomers",
          }
        );
      }

      return result;
    },


    async resolveLoyalty(
      customer,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          loyaltyRuntime,

        ownerName:
          "loyaltyRuntime",

        method:
          "resolveLoyalty",

        operation:
          "resolveLoyalty",

        args: [
          customer,
          createScope(context),
        ],
      });
    },


    async openSession(
      payload = {},
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          sessionBridge,

        ownerName:
          "sessionBridge",

        method:
          "openSession",

        operation:
          "openSession",

        args: [
          createScope(context),
          payload,
        ],
      });
    },


    async resolveCurrentSession(
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          sessionBridge,

        ownerName:
          "sessionBridge",

        method:
          "resolveCurrentSession",

        operation:
          "resolveCurrentSession",

        args: [
          createScope(context),
        ],
      });
    },


    async closeSession(
      cashSessionUuid,
      payload = {},
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          sessionBridge,

        ownerName:
          "sessionBridge",

        method:
          "closeSession",

        operation:
          "closeSession",

        args: [
          createScope(context),
          cashSessionUuid,
          payload,
        ],
      });
    },


    async getSessionHistory(
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          sessionBridge,

        ownerName:
          "sessionBridge",

        method:
          "getSessionHistory",

        operation:
          "getSessionHistory",

        args: [
          createScope(context),
        ],
      });
    },


    async getSessionReport(
      cashSessionUuid,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          sessionBridge,

        ownerName:
          "sessionBridge",

        method:
          "getSessionReport",

        operation:
          "getSessionReport",

        args: [
          createScope(context),
          cashSessionUuid,
        ],
      });
    },


    async checkout(
      {
        payments = [],
        customer = null,
        loyalty = null,
        metadata = {},
      } = {},
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          cartRuntime,

        ownerName:
          "cartRuntime",

        method:
          "checkout",

        operation:
          "checkout",

        args: [
          {
            payments,
            customer,
            loyalty,
            metadata,
          },

          createScope(context),
        ],
      });
    },


    async resolveReceipt(
      receiptReference,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          receiptRuntime,

        ownerName:
          "receiptRuntime",

        method:
          "resolveReceipt",

        operation:
          "resolveReceipt",

        args: [
          receiptReference,
          createScope(context),
        ],
      });
    },


    async applyDiscount(
      receipt,
      discountIntent,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          receiptRuntime,

        ownerName:
          "receiptRuntime",

        method:
          "applyDiscount",

        operation:
          "applyDiscount",

        args: [
          receipt,
          discountIntent,
          createScope(context),
        ],
      });
    },


    async redeemLoyaltyBenefit(
      receipt,
      benefit,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          loyaltyRuntime,

        ownerName:
          "loyaltyRuntime",

        method:
          "redeemBenefit",

        operation:
          "redeemLoyaltyBenefit",

        args: [
          receipt,
          benefit,
          createScope(context),
        ],
      });
    },


    async listPayments(
      receipt,
      context = {}
    ) {
      assertScope(context);

      const scope =
        createScope(context);

      const receiptUuid =
        normalizeReceiptUuid(
          receipt
        );

      return invokeCapability({
        owner:
          receiptRuntime,

        ownerName:
          "receiptRuntime",

        method:
          "listPayments",

        operation:
          "listPayments",

        args: [
          {
            receiptUuid,

            companyUuid:
              scope.companyUuid,
          },
        ],
      });
    },


    async submitPayment(
      receipt,
      payment,
      context = {}
    ) {
      assertScope(context);

      const scope =
        createScope(context);

      const receiptUuid =
        normalizeReceiptUuid(
          receipt
        );

      const normalizedPayment =
        normalizePaymentIntent(
          payment
        );

      return invokeCapability({
        owner:
          receiptRuntime,

        ownerName:
          "receiptRuntime",

        method:
          "submitPayment",

        operation:
          "submitPayment",

        args: [
          {
            receiptUuid,

            companyUuid:
              scope.companyUuid,

            meansPayment:
              normalizedPayment
                .meansPayment,

            amount:
              normalizedPayment
                .amount,

            checkFormat:
              normalizedPayment
                .checkFormat,
          },
        ],
      });
    },


    async printReceipt(
      receipt,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          receiptRuntime,

        ownerName:
          "receiptRuntime",

        method:
          "printReceipt",

        operation:
          "printReceipt",

        args: [
          receipt,
          createScope(context),
        ],
      });
    },


    async returnProduct(
      payload,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          receiptRuntime,

        ownerName:
          "receiptRuntime",

        method:
          "returnProduct",

        operation:
          "returnProduct",

        args: [
          payload,
          createScope(context),
        ],

        optionalBusinessCapability:
          true,
      });
    },


    async refund(
      payload,
      context = {}
    ) {
      assertScope(context);

      return invokeCapability({
        owner:
          receiptRuntime,

        ownerName:
          "receiptRuntime",

        method:
          "refund",

        operation:
          "refund",

        args: [
          payload,
          createScope(context),
        ],

        optionalBusinessCapability:
          true,
      });
    },


    getCapabilities() {
      return Object.freeze({
        productResolution:
          typeof productResolver
            === "function",

        productSearch:
          typeof productSearchProvider
            === "function",

        cart:
          hasMethod(
            cartRuntime,
            "loadCart"
          ),

        checkout:
          hasMethod(
            cartRuntime,
            "checkout"
          ),

        customer:
          hasMethod(
            customerRuntime,
            "resolveCustomer"
          ),

        customerSearch:
          hasMethod(
            customerRuntime,
            "searchCustomers"
          ),

        loyalty:
          hasMethod(
            loyaltyRuntime,
            "resolveLoyalty"
          ),

        cashSession:
          hasMethod(
            sessionBridge,
            "resolveCurrentSession"
          ),

        receipt:
          hasMethod(
            receiptRuntime,
            "resolveReceipt"
          ),

        receiptDiscount:
          hasMethod(
            receiptRuntime,
            "applyDiscount"
          ),

        loyaltyRedemption:
          hasMethod(
            loyaltyRuntime,
            "redeemBenefit"
          ),

        receiptPayments:
          (
            hasMethod(
              receiptRuntime,
              "listPayments"
            )
            && hasMethod(
              receiptRuntime,
              "submitPayment"
            )
          ),

        receiptPrinting:
          hasMethod(
            receiptRuntime,
            "printReceipt"
          ),

        returns:
          hasMethod(
            receiptRuntime,
            "returnProduct"
          ),

        refunds:
          hasMethod(
            receiptRuntime,
            "refund"
          ),
      });
    },
  };


  return Object.freeze(
    runtime
  );
}


export function isRetailPOSRuntime(
  runtime
) {
  return (
    runtime !== null
    && typeof runtime
      === "object"

    && typeof runtime
      .resolveProductByCode
      === "function"

    && typeof runtime
      .scanProduct
      === "function"

    && typeof runtime
      .searchProducts
      === "function"

    && typeof runtime
      .loadCart
      === "function"

    && typeof runtime
      .addProduct
      === "function"

    && typeof runtime
      .checkout
      === "function"

    && typeof runtime
      .resolveCurrentSession
      === "function"

    && typeof runtime
      .getCapabilities
      === "function"
  );
}


function normalizeReceiptUuid(
  receipt
) {
  const candidate =
    typeof receipt === "string"
      ? receipt
      : (
        receipt?.receiptUuid
        ?? receipt?.receipt_uuid
        ?? receipt?.uuid
      );

  if (
    typeof candidate !== "string"
    || candidate.trim() === ""
  ) {
    throw new TypeError(
      "receiptUuid must be a non-empty string."
    );
  }

  return candidate.trim();
}


function normalizePaymentIntent(
  payment
) {
  if (
    !payment
    || typeof payment !== "object"
    || Array.isArray(payment)
  ) {
    throw new TypeError(
      "payment must be an object."
    );
  }

  const meansPayment =
    payment.meansPayment
    ?? payment.means_payment;

  if (
    typeof meansPayment !== "string"
    || meansPayment.trim() === ""
  ) {
    throw new TypeError(
      "meansPayment must be a non-empty string."
    );
  }

  const amount =
    Number(
      payment.amount
    );

  if (
    !Number.isFinite(amount)
    || amount <= 0
  ) {
    throw new TypeError(
      "amount must be a positive number."
    );
  }

  return Object.freeze({
    meansPayment:
      meansPayment.trim(),

    amount,

    checkFormat:
      payment.checkFormat
      ?? payment.check_format
      ?? true,
  });
}


function createScope(
  context
) {
  return Object.freeze({
    companyUuid:
      context.companyUuid,

    activityUuid:
      context.activityUuid,

    cashSessionUuid:
      context.cashSessionUuid
      ?? null,

    terminalUuid:
      context.terminalUuid
      ?? null,

    deviceUuid:
      context.deviceUuid
      ?? null,

    userUuid:
      context.userUuid
      ?? null,
  });
}


function assertScope(
  context
) {
  if (
    !context
    || typeof context
      !== "object"
  ) {
    throw new RetailPOSRuntimeError(
      "Retail POS context must be an object.",
      {
        code:
          "INVALID_RETAIL_POS_CONTEXT",
      }
    );
  }

  assertNonEmptyString(
    context.companyUuid,
    "companyUuid"
  );

  assertNonEmptyString(
    context.activityUuid,
    "activityUuid"
  );
}


function assertPositiveQuantity(
  quantity
) {
  if (
    !Number.isFinite(quantity)
    || quantity <= 0
  ) {
    throw new RetailPOSRuntimeError(
      "Retail POS quantity must be a positive number.",
      {
        code:
          "INVALID_RETAIL_POS_QUANTITY",

        operation:
          "addProduct",
      }
    );
  }
}


function assertNonEmptyString(
  value,
  field
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new RetailPOSRuntimeError(
      `${field} must be a non-empty string.`,
      {
        code:
          "INVALID_RETAIL_POS_ARGUMENT",
      }
    );
  }
}


function assertOptionalFunction(
  value,
  name
) {
  if (
    value !== null
    && typeof value
      !== "function"
  ) {
    throw new TypeError(
      `${name} must be a function or null.`
    );
  }
}


function assertOptionalObject(
  value,
  name
) {
  if (
    value !== null
    && (
      typeof value
        !== "object"
      || Array.isArray(value)
    )
  ) {
    throw new TypeError(
      `${name} must be an object or null.`
    );
  }
}


function hasMethod(
  owner,
  method
) {
  return (
    owner !== null
    && typeof owner
      === "object"
    && typeof owner[method]
      === "function"
  );
}


function requireCapability(
  capability,
  name,
  operation
) {
  if (
    typeof capability
      !== "function"
  ) {
    throw new RetailPOSRuntimeError(
      `Retail POS capability "${name}" is unavailable.`,
      {
        code:
          "RETAIL_POS_CAPABILITY_UNAVAILABLE",

        operation,
      }
    );
  }

  return capability;
}


function invokeCapability({
  owner,
  ownerName,
  method,
  operation,
  args,
  optionalBusinessCapability =
    false,
}) {
  if (
    !hasMethod(
      owner,
      method
    )
  ) {
    throw new RetailPOSRuntimeError(
      optionalBusinessCapability
        ? (
          `Retail POS business capability "${method}" `
          + "is not certified by the injected Runtime."
        )
        : (
          `Retail POS capability "${ownerName}.${method}" `
          + "is unavailable."
        ),
      {
        code:
          optionalBusinessCapability
            ? "RETAIL_POS_BUSINESS_CAPABILITY_UNAVAILABLE"
            : "RETAIL_POS_CAPABILITY_UNAVAILABLE",

        operation,
      }
    );
  }

  return invoke(
    operation,
    () =>
      owner[method](
        ...args
      )
  );
}


async function invoke(
  operation,
  callback
) {
  try {
    return await Promise.resolve(
      callback()
    );
  } catch (error) {
    if (
      error instanceof
        RetailPOSRuntimeError
    ) {
      throw error;
    }

    throw new RetailPOSRuntimeError(
      `Retail POS operation "${operation}" failed.`,
      {
        code:
          "RETAIL_POS_OPERATION_FAILED",

        operation,
        cause:
          error,
      }
    );
  }
}
